import { Switch, useRouteMatch } from 'react-router-dom';
import * as Avatar from '@givebutter/primitives/avatar';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal } from '@givebutter/util';
import { faEllipsis, faHeart } from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight, faRepeat } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as CancelRecurringPlanAlertDialog from 'assets/js/pages/recurring-payments/CancelRecurringPlanAlertDialog';
import { PastRecurringPlanTransactions } from 'assets/js/pages/recurring-payments/PastRecurringPlanTransactions';
import { RecurringPlanEditableAppeal } from 'assets/js/pages/recurring-payments/RecurringPlanEditableAppeal';
import { RecurringPlanEditableFund } from 'assets/js/pages/recurring-payments/RecurringPlanEditableFund';
import { RecurringPlanStatusBadge } from 'assets/js/pages/recurring-payments/RecurringPlanStatusBadge';
import * as ReadOnlyPaymentDetails from 'assets/js/pages/transactions/ReadOnlyPaymentDetails';
import { SupporterDetails } from 'assets/js/pages/transactions/SupporterDetails';
import { TransactionCampaign } from 'assets/js/pages/transactions/TransactionCampaign';
import { TransactionDrawerRouter } from 'assets/js/pages/transactions/TransactionDrawerRouter';
import { TransactionMethodPresentational } from 'assets/js/pages/transactions/TransactionMethod';
import * as Drawer from 'components/drawer';
import { DrawerIncrementor } from 'components/drawer/DrawerIncrementor';
import * as DropdownMenu from 'components/dropdown-menu';
import { Route } from 'components/router/Route';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentPlanOrById } from 'customHooks/useCurrentPlanOrById';
import { isoDateFromServer } from 'helpers/dates';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { PolymorphicTransaction, Transaction, TransactionPlanStatus } from 'types/domain';

const HIDE_NEXT_PAYMENT_STATUSES: TransactionPlanStatus[] = ['cancelled', 'paused', 'ended'];

const RecurringPlanDrawer = () => {
    const currencySymbol = useCurrencySymbol();
    const match = useRouteMatch();
    const { data: plan } = useCurrentPlanOrById();
    const hideNextPayment = !HIDE_NEXT_PAYMENT_STATUSES.includes(plan.status);
    const isIndex = match.path.includes('/accounts/:account_id/plans');

    return (
        <>
            <Drawer.Header>
                <div className={'w-full'}>
                    <div className={'flex flex-col gap-4'}>
                        <BoxIcon size="lg">
                            <FontAwesomeIcon size={'lg'} icon={faRepeat} />
                        </BoxIcon>
                        <div className={'flex items-center gap-2'}>
                            <p className={'text-2xl font-medium'}>
                                {currencySymbol}
                                {maskDecimal(plan.amount)}
                            </p>
                            <Badge>{capitalize(plan.frequency)}</Badge>
                            <RecurringPlanStatusBadge plan={plan} />
                        </div>
                        <p>
                            Recurring plan started on{' '}
                            {isoDateFromServer(plan.start_at).toLocaleString(DateTime.DATE_FULL)}
                        </p>
                        <div className={'flex gap-4'}>
                            <Box className={'flex flex-1 flex-col gap-2 p-2.5'}>
                                <p className={'text-xl font-medium'}>${plan.stats.ytd_total}</p>
                                <p className={'text-xs font-medium text-gray-600'}>
                                    Year-to-date total
                                </p>
                            </Box>
                            <Box className={'flex flex-1 flex-col gap-2 p-2.5'}>
                                <p className={'text-xl font-medium'}>
                                    ${plan.stats.all_time_total}
                                </p>
                                <p className={'text-xs font-medium text-gray-600'}>
                                    Total contributed
                                </p>
                            </Box>
                            <Box className={'flex flex-1 flex-col gap-2 p-2.5'}>
                                <p className={'text-xl font-medium'}>
                                    ${plan.stats.estimated_annual_total}
                                </p>
                                <p className={'text-xs font-medium text-gray-600'}>
                                    Expected annual total
                                </p>
                            </Box>
                        </div>
                    </div>
                    <div className={'absolute right-8 top-4 flex items-start gap-2'}>
                        {isIndex && <DrawerIncrementor resource="transaction_plans" />}
                        <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild={true}>
                                <Button
                                    size={'sm'}
                                    color={'neutral'}
                                    shape={'square'}
                                    variant={'outline'}
                                    aria-label="More"
                                >
                                    <FontAwesomeIcon icon={faEllipsis} />
                                </Button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content sideOffset={4} align={'end'}>
                                <CancelRecurringPlanAlertDialog.Root plan={plan}>
                                    <CancelRecurringPlanAlertDialog.Trigger asChild={true}>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            disabled={plan.status === 'cancelled'}
                                        >
                                            Cancel Plan
                                        </DropdownMenu.Item>
                                    </CancelRecurringPlanAlertDialog.Trigger>
                                </CancelRecurringPlanAlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    </div>
                </div>
            </Drawer.Header>
            <Drawer.Body>
                <div className={'flex flex-col gap-6'}>
                    <div className={'flex flex-col gap-4'}>
                        {plan?.contact ? <SupporterDetails contact={plan.contact} /> : null}
                        {plan.campaign && (
                            <TransactionCampaign
                                transaction={{ campaign: plan.campaign } as unknown as Transaction}
                            />
                        )}
                        <RecurringPlanEditableFund />
                        <RecurringPlanEditableAppeal />
                        <div className={'flex flex-col gap-2'}>
                            <p className={'text-sm font-medium text-gray-800'}>Payment details</p>
                            <Collapsible.Root>
                                <Collapsible.Trigger>
                                    <div className={'flex items-center gap-2'}>
                                        <FontAwesomeIcon
                                            className={
                                                'group-radix-state-open:rotate-90 text-gray-600'
                                            }
                                            icon={faChevronRight}
                                        />
                                        <TransactionMethodPresentational
                                            transaction={plan as unknown as PolymorphicTransaction}
                                        />
                                        {plan.payment_method?.is_expired && (
                                            <Badge color="error">
                                                Card expired{' '}
                                                {isoDateFromServer(
                                                    plan.payment_method.cc_expire,
                                                ).toLocaleString(DateTime.DATE_MED)}
                                            </Badge>
                                        )}
                                        {!plan.payment_method?.is_expired &&
                                            plan.payment_method?.is_expiring_soon && (
                                                <Badge color="warning">
                                                    Card expiring{' '}
                                                    {isoDateFromServer(
                                                        plan.payment_method.cc_expire,
                                                    ).toLocaleString(DateTime.DATE_MED)}
                                                </Badge>
                                            )}
                                    </div>
                                </Collapsible.Trigger>
                                <Collapsible.Content className={'bg-gray-25 rounded-lg p-3'}>
                                    <ReadOnlyPaymentDetails.Root
                                        transaction={
                                            {
                                                first_name: plan.first_name,
                                                last_name: plan.last_name,
                                                email: plan.email,
                                                payment_method: plan.payment_method,
                                            } as PolymorphicTransaction
                                        }
                                    >
                                        <ReadOnlyPaymentDetails.Name />
                                        <ReadOnlyPaymentDetails.Phone />
                                        <ReadOnlyPaymentDetails.Email />
                                        <ReadOnlyPaymentDetails.Method />
                                        <ReadOnlyPaymentDetails.CcExpire />
                                        <ReadOnlyPaymentDetails.Daf />
                                    </ReadOnlyPaymentDetails.Root>
                                </Collapsible.Content>
                            </Collapsible.Root>
                        </div>
                    </div>
                    <Separator />
                    {hideNextPayment && (
                        <div className={'flex flex-col gap-6'}>
                            <p className={'text-2xl font-medium'}>Next payment</p>
                            <Box className={'flex gap-4 p-4'}>
                                <Avatar.Root>
                                    <Avatar.Fallback
                                        className={'bg-blue-500'}
                                        aria-label="Next payment icon"
                                    >
                                        <FontAwesomeIcon className={'text-white'} icon={faHeart} />
                                    </Avatar.Fallback>
                                </Avatar.Root>
                                <div
                                    className={'flex flex-grow items-center justify-between gap-4'}
                                >
                                    <div className={'flex flex-col'}>
                                        <p className={'text-sm font-medium'}>
                                            {currencySymbol}
                                            {maskDecimal(plan.amount)}
                                        </p>
                                        <p className={'text-sm text-gray-500'}>Next payment</p>
                                    </div>
                                    <p>
                                        <p className={'text-sm text-gray-500'}>
                                            {isoDateFromServer(plan.next_bill_date).toLocaleString(
                                                DateTime.DATE_SHORT,
                                            )}
                                        </p>
                                    </p>
                                </div>
                            </Box>
                        </div>
                    )}
                    <PastRecurringPlanTransactions />
                </div>
            </Drawer.Body>
            <Drawer.Footer className={'sm:hidden'}>
                <Drawer.Cancel asChild={true}>
                    <Button variant={'outline'} color={'neutral'}>
                        Close
                    </Button>
                </Drawer.Cancel>
            </Drawer.Footer>
        </>
    );
};

const RecurringPlanDrawerRouter = () => {
    const match = useRouteMatch();

    return (
        <Switch>
            <Route
                path={`${match.path}/transactions/:transaction_id`}
                component={TransactionDrawerRouter}
            />
            <Route path={match.path} component={RecurringPlanDrawer} />
        </Switch>
    );
};

export { RecurringPlanDrawerRouter as RecurringPlanDrawer };
